/**
 * StickyNoteCard
 * --------------
 * Reusable polished sticky-note presentation for HRSN screening notes.
 *
 * The visual language matches the polished version in the uploader's
 * screening detail modal (commit f1a2cbb): peach pastel card, left
 * accent rail, circular StickyNote icon, uppercase domain label,
 * serif-italic note body wrapped in curly quotes, optional
 * 'Captured by X on Y' attribution line.
 *
 * Two sizes:
 *   - 'full'    — used in the uploader detail modal (default)
 *   - 'compact' — used in admin AgentDetailModal accordions and
 *                  AuditLogPage SUBMIT_SCREENING expansions
 *
 * Note CONTENT is never modified — only the visual presentation.
 * Bilingual via the existing 'screening.detail.note.capturedBy' key.
 */

import type { ComponentType, JSX } from 'react';
import { StickyNote, FileText, Home, Building2, Zap, UtensilsCrossed, Car, Briefcase, GraduationCap, Shield, Accessibility } from 'lucide-react';
import { humanizeDomainName, type HrsnDomain } from '@/features/screening/answerHumanizer';
import { useT, useLang } from '@/i18n';

type IconType = ComponentType<{ className?: string }>;
const DOMAIN_ICON: Record<string, IconType> = {
  living_situation: Home,
  housing_conditions: Building2,
  utilities: Zap,
  food: UtensilsCrossed,
  transportation: Car,
  career: Briefcase,
  school: GraduationCap,
  safety: Shield,
  physical_ability: Accessibility,
};

export interface StickyNoteCardProps {
  /** Domain key, e.g. 'living_situation' — used for label + mini icon. */
  domain: string;
  /** The note content (rendered exactly, no edits). */
  text: string;
  /** Optional agent display name for attribution. */
  capturedBy?: string;
  /** Optional ISO date for 'Captured by X on Y' suffix. */
  capturedAt?: string;
  /** Visual size. 'full' is the polished detail-modal size, 'compact' is denser. */
  size?: 'compact' | 'full';
}

function formatDate(iso: string | null | undefined, lang: 'en' | 'es'): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(lang === 'es' ? 'es-US' : 'en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch {
    return iso;
  }
}

export function StickyNoteCard({
  domain,
  text,
  capturedBy,
  capturedAt,
  size = 'full',
}: StickyNoteCardProps): JSX.Element {
  const t = useT();
  const lang = useLang();
  const DomIcon: IconType = DOMAIN_ICON[domain] ?? FileText;
  const humanDomainName = humanizeDomainName(domain as HrsnDomain, lang);
  const showAttribution = !!(capturedBy || capturedAt);

  const isCompact = size === 'compact';
  const padding = isCompact ? 'p-3 pl-4' : 'p-4 pl-5';
  const iconBox = isCompact ? 'h-8 w-8' : 'h-9 w-9';
  const iconSize = isCompact ? 'h-4 w-4' : 'h-5 w-5';
  const noteText = isCompact ? 'text-sm leading-relaxed' : 'text-base leading-relaxed';

  return (
    <article
      className={`relative rounded-[12px] bg-nav-amberTint border-[1.5px] border-[#e6cf9a] ${padding} transition-shadow hover:shadow-md`}
    >
      {/* warm left accent bar */}
      <span aria-hidden className="absolute left-0 top-3 bottom-3 w-1 rounded-r-full bg-nav-amber/80" />
      <div className="flex items-start gap-3">
        <div className={`shrink-0 inline-flex items-center justify-center ${iconBox} rounded-full bg-white/70 border border-[#e6cf9a]`}>
          <StickyNote className={`${iconSize} text-nav-amberText`} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-1">
            <DomIcon className="h-3 w-3 text-nav-amberText/70" />
            <span className="font-head text-[11px] uppercase tracking-wider font-bold text-nav-amberText">
              {humanDomainName}
            </span>
          </div>
          <p
            className={`${noteText} text-nav-navy italic`}
            style={{ fontFamily: '"Georgia", "Charter", "Iowan Old Style", serif' }}
          >
            &ldquo;{text}&rdquo;
          </p>
          {showAttribution && !isCompact && (
            <p className="mt-2 text-[11px] text-nav-slate text-right">
              {t('screening.detail.note.capturedBy', {
                agent: capturedBy ?? '—',
                date: formatDate(capturedAt, lang),
              })}
            </p>
          )}
          {showAttribution && isCompact && (
            <p className="mt-1.5 text-[10px] text-nav-slate text-right">
              {t('screening.detail.note.capturedBy', {
                agent: capturedBy ?? '—',
                date: formatDate(capturedAt, lang),
              })}
            </p>
          )}
        </div>
      </div>
    </article>
  );
}
